import { SearchPageCity } from './searchPages'

/**
 * Compact, opaque-to-the-user codes for embedding an approximate city
 * hint in bare-CTA WhatsApp prefill messages without bloating the visible
 * text. Used as a suffix `… · mty` on the message — the LangGraph analysis
 * prompt decodes it via a mirrored table on the server side.
 *
 * Why a hand-built map instead of the kebab-case slug:
 *   - Slugs like `san-pedro-garza-garcia` are 22 chars; the whole point of
 *     this exercise is to be unobtrusive in the customer-visible draft.
 *   - These codes match the abbreviations Mexicans already use in chat
 *     (`mty`, `cdmx`, `gdl`, `spgg`, `tj`), so a user who sees the suffix
 *     in their WhatsApp draft reads "city marker" not "tracking garbage"
 *     — unlike the old `· esvk` session-token suffix we removed.
 *
 * Cross-repo mirror requirement: `spotme-server/src/constants/cityHintCodes.ts`
 * MUST be byte-equivalent. The server uses this map to populate the
 * `CITY_HINT_DECODE` table inside the analysis prompt. Drift means the
 * chatbot can't decode codes the frontend emitted, or worse, decodes them
 * to the wrong city. Document in CLAUDE.md "Cross-repo constants" alongside
 * `searchPages` / `onDemandCoverage` / `classifyAttribution`.
 *
 * When adding a new `SearchPageCity`:
 *   1. Pick a 2-5 char lowercase code. Match common Mexican abbreviation
 *      conventions where possible (Linea Vehicular, postal codes, etc).
 *   2. Verify uniqueness against existing values — a collision means two
 *      cities decode the same way and the hint becomes ambiguous.
 *   3. Mirror the entry in `spotme-server/src/constants/cityHintCodes.ts`.
 *   4. The server analysis prompt is rebuilt from the table at module
 *      load — no separate prompt edit needed.
 */
export const CITY_HINT_CODES: Readonly<Record<SearchPageCity, string>> = {
  [SearchPageCity.Base]: '',

  // Primary metros
  [SearchPageCity.Monterrey]: 'mty',
  [SearchPageCity.CiudadDeMexico]: 'cdmx',
  [SearchPageCity.Guadalajara]: 'gdl',
  [SearchPageCity.Tijuana]: 'tj',
  [SearchPageCity.Puebla]: 'pue',
  [SearchPageCity.Queretaro]: 'qro',
  [SearchPageCity.Leon]: 'leon',
  [SearchPageCity.Chihuahua]: 'chi',
  [SearchPageCity.Merida]: 'mer',
  [SearchPageCity.Cancun]: 'cun',
  [SearchPageCity.Aguascalientes]: 'ags',
  [SearchPageCity.Hermosillo]: 'hmo',
  [SearchPageCity.PuertoVallarta]: 'pvr',
  [SearchPageCity.Toluca]: 'tol',
  [SearchPageCity.LosCabos]: 'cab',

  // Newer major cities
  [SearchPageCity.CiudadJuarez]: 'cjs',
  [SearchPageCity.Cuernavaca]: 'cvc',
  [SearchPageCity.Saltillo]: 'slw',
  [SearchPageCity.SanLuisPotosi]: 'slp',
  [SearchPageCity.Mexicali]: 'mxli',
  [SearchPageCity.Torreon]: 'trc',
  [SearchPageCity.PlayaDelCarmen]: 'pdc',
  [SearchPageCity.Veracruz]: 'ver',
  [SearchPageCity.Tampico]: 'tam',
  [SearchPageCity.Villahermosa]: 'vsa',
  [SearchPageCity.Morelia]: 'mor',
  [SearchPageCity.Pachuca]: 'pac',
  [SearchPageCity.Reynosa]: 'rey',
  [SearchPageCity.Mazatlan]: 'maz',
  [SearchPageCity.Celaya]: 'cel',

  // Monterrey metro
  [SearchPageCity.Apodaca]: 'apod',
  [SearchPageCity.Cumbres]: 'cum',
  [SearchPageCity.Escobedo]: 'esc',
  [SearchPageCity.Guadalupe]: 'gpe',
  [SearchPageCity.SantaCatarina]: 'scat',
  [SearchPageCity.SanNicolas]: 'snic',
  [SearchPageCity.SanPedro]: 'spgg',

  // Guadalajara metro
  [SearchPageCity.Zapopan]: 'zap',
  [SearchPageCity.Tlaquepaque]: 'tlaq',
  [SearchPageCity.Tonala]: 'ton',
  [SearchPageCity.Tlajomulco]: 'tlaj',
  [SearchPageCity.ElSalto]: 'esa',

  // Toluca metro
  [SearchPageCity.Metepec]: 'mete',
  [SearchPageCity.Zinacantepec]: 'zin',

  // Puebla metro
  [SearchPageCity.SanAndresCholula]: 'sach',
  [SearchPageCity.SanPedroCholula]: 'spch',

  // Tijuana metro
  [SearchPageCity.PlayasDeRosarito]: 'ros',
  [SearchPageCity.Tecate]: 'tec',

  // Querétaro metro
  [SearchPageCity.Corregidora]: 'cor',
  [SearchPageCity.ElMarques]: 'mq',

  // Aguascalientes metro
  [SearchPageCity.JesusMaria]: 'jm',

  // Mérida metro
  [SearchPageCity.Kanasin]: 'kan',

  // Saltillo metro
  [SearchPageCity.RamosArizpe]: 'ra',

  // San Luis Potosí metro
  [SearchPageCity.Soledad]: 'sol',

  // Cuernavaca metro
  [SearchPageCity.Jiutepec]: 'jiu',
  [SearchPageCity.Temixco]: 'tmx',

  // Torreón metro
  [SearchPageCity.GomezPalacio]: 'gp',
  [SearchPageCity.Lerdo]: 'ler',

  // León / Bajío
  [SearchPageCity.Irapuato]: 'ira',
  [SearchPageCity.Silao]: 'sil',

  // Tampico metro
  [SearchPageCity.CiudadMadero]: 'cmad',

  // Baja California
  [SearchPageCity.Ensenada]: 'ens',

  // Baja California Sur
  [SearchPageCity.LaPaz]: 'lp',
  [SearchPageCity.CiudadConstitucion]: 'cc',

  // Sonora
  [SearchPageCity.CiudadObregon]: 'co',
  [SearchPageCity.Nogales]: 'nog',
  [SearchPageCity.SanLuisRioColorado]: 'slrc',
  [SearchPageCity.Guaymas]: 'gua',
  [SearchPageCity.Navojoa]: 'nav',
  [SearchPageCity.AguaPrieta]: 'ap',
  [SearchPageCity.Caborca]: 'cabo',

  // Sinaloa
  [SearchPageCity.Culiacan]: 'cul',
  [SearchPageCity.LosMochis]: 'lm',
  [SearchPageCity.Guasave]: 'guas',
  [SearchPageCity.Guamuchil]: 'guam',

  // Chihuahua
  [SearchPageCity.Delicias]: 'del',
  [SearchPageCity.Cuauhtemoc]: 'cua',
  [SearchPageCity.HidalgoDelParral]: 'hdp',
  [SearchPageCity.NuevoCasasGrandes]: 'ncg',

  // Coahuila
  [SearchPageCity.Monclova]: 'mon',
  [SearchPageCity.PiedrasNegras]: 'pn',
  [SearchPageCity.Acuna]: 'acu',

  // Durango
  [SearchPageCity.Durango]: 'dur',

  // Zacatecas
  [SearchPageCity.Zacatecas]: 'zac',
  [SearchPageCity.Fresnillo]: 'fre',

  // Nuevo León
  [SearchPageCity.Linares]: 'lin',
  [SearchPageCity.Montemorelos]: 'mont',

  // Tamaulipas
  [SearchPageCity.CiudadVictoria]: 'cv',
  [SearchPageCity.Matamoros]: 'mat',
  [SearchPageCity.NuevoLaredo]: 'nl',
  [SearchPageCity.Altamira]: 'alt',
  [SearchPageCity.ElMante]: 'em',

  // San Luis Potosí
  [SearchPageCity.CiudadValles]: 'ciu',
  [SearchPageCity.Matehuala]: 'mate',
  [SearchPageCity.Rioverde]: 'rio',

  // Guanajuato
  [SearchPageCity.Guanajuato]: 'guan',
  [SearchPageCity.Salamanca]: 'sal',

  // Querétaro
  [SearchPageCity.SanJuanDelRio]: 'sjdr',

  // Jalisco
  [SearchPageCity.LagosDeMoreno]: 'ldm',
  [SearchPageCity.TepatitlanDeMorelos]: 'tdm',
  [SearchPageCity.CiudadGuzman]: 'cg',
  [SearchPageCity.Ocotlan]: 'oco',
  [SearchPageCity.AutlanDeNavarro]: 'adn',
  [SearchPageCity.Arandas]: 'ara',

  // Colima
  [SearchPageCity.Colima]: 'col',
  [SearchPageCity.Manzanillo]: 'man',
  [SearchPageCity.Tecoman]: 'teco',

  // Nayarit
  [SearchPageCity.Tepic]: 'tep',
  [SearchPageCity.Compostela]: 'com',
  [SearchPageCity.SantiagoIxcuintla]: 'si',
  [SearchPageCity.Bucerias]: 'buc',

  // Michoacán
  [SearchPageCity.Uruapan]: 'uru',
  [SearchPageCity.Zamora]: 'zam',
  [SearchPageCity.LazaroCardenas]: 'lc',
  [SearchPageCity.Apatzingan]: 'apa',
  [SearchPageCity.Zitacuaro]: 'zit',
  [SearchPageCity.LaPiedad]: 'la',
  [SearchPageCity.Sahuayo]: 'sah',
  [SearchPageCity.CiudadHidalgo]: 'ch',

  // Estado de México
  [SearchPageCity.Atlacomulco]: 'atl',

  // Hidalgo
  [SearchPageCity.Tulancingo]: 'tul',
  [SearchPageCity.TulaDeAllende]: 'tda',
  [SearchPageCity.HuejutlaDeReyes]: 'hdr',

  // Puebla
  [SearchPageCity.Tehuacan]: 'teh',
  [SearchPageCity.SanMartinTexmelucan]: 'smt',
  [SearchPageCity.Atlixco]: 'atli',
  [SearchPageCity.Teziutlan]: 'tez',

  // Tlaxcala
  [SearchPageCity.Tlaxcala]: 'tla',
  [SearchPageCity.Apizaco]: 'api',
  [SearchPageCity.Huamantla]: 'hua',

  // Morelos
  [SearchPageCity.Cuautla]: 'cuau',

  // Guerrero
  [SearchPageCity.Acapulco]: 'aca',
  [SearchPageCity.Chilpancingo]: 'chil',
  [SearchPageCity.Iguala]: 'igu',

  // Oaxaca
  [SearchPageCity.Oaxaca]: 'oax',
  [SearchPageCity.Tuxtepec]: 'tux',
  [SearchPageCity.Juchitan]: 'juc',
  [SearchPageCity.SalinaCruz]: 'sc',
  [SearchPageCity.HuajuapanDeLeon]: 'hdl',

  // Veracruz
  [SearchPageCity.Xalapa]: 'xal',
  [SearchPageCity.Coatzacoalcos]: 'coa',
  [SearchPageCity.Cordoba]: 'cord',
  [SearchPageCity.Orizaba]: 'ori',
  [SearchPageCity.PozaRica]: 'pr',
  [SearchPageCity.Minatitlan]: 'min',
  [SearchPageCity.Tuxpan]: 'tuxp',
  [SearchPageCity.SanAndresTuxtla]: 'sat',
  [SearchPageCity.MartinezDeLaTorre]: 'mdlt',

  // Tabasco
  [SearchPageCity.Cardenas]: 'car',
  [SearchPageCity.Comalcalco]: 'coma',

  // Chiapas
  [SearchPageCity.TuxtlaGutierrez]: 'tg',
  [SearchPageCity.Tapachula]: 'tap',
  [SearchPageCity.SanCristobalDeLasCasas]: 'scdlc',
  [SearchPageCity.Comitan]: 'comi',

  // Campeche
  [SearchPageCity.Campeche]: 'cam',
  [SearchPageCity.CiudadDelCarmen]: 'cdc',

  // Yucatán
  [SearchPageCity.Valladolid]: 'val',
  [SearchPageCity.Tizimin]: 'tiz',

  // Quintana Roo
  [SearchPageCity.Chetumal]: 'che',
  [SearchPageCity.Cozumel]: 'coz'
}

/**
 * The suffix-separator we emit before the code. Mid-dot interpunct, same
 * shape as the old (now-removed) session-token suffix. The server-side
 * extractor looks for this exact pattern at the END of the message.
 */
export const CITY_HINT_SEPARATOR = ' · '

/**
 * Build the wire-format suffix for a resolved SearchPageCity. Returns ''
 * when the city is `Base` or absent from the map (defensive; shouldn't
 * happen in practice since the type is exhaustive).
 */
export function buildCityHintSuffix(city: SearchPageCity): string {
  const code = CITY_HINT_CODES[city]
  if (!code) return ''
  return `${CITY_HINT_SEPARATOR}${code}`
}
